import React, { useState, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import Snackbar from '@mui/joy/Snackbar';
import Sheet from '@mui/joy/Sheet';
import Grid from '@mui/joy/Grid';
import IconButton from '@mui/joy/IconButton';
import Alert from '@mui/joy/Alert';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Tooltip from '@mui/joy/Tooltip';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import CircularProgress from '@mui/joy/CircularProgress';
import Stack from '@mui/joy/Stack';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import { getSettings, setSettings } from '../../utils/settings';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ClassIcon from '@mui/icons-material/Class';
import SeedOutlinedIcon from '@mui/icons-material/SpaOutlined';
import OpacityOutlinedIcon from '@mui/icons-material/OpacityOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/Inventory2Outlined';

const ITEM_TYPES = [
  { key: 'PRODUCT-SEED', label: 'Seed Products', icon: <SeedOutlinedIcon />, color: '#57C84D' },
  { key: 'PRODUCT-CLONE', label: 'Clone Products', icon: <OpacityOutlinedIcon />, color: '#BC13FE' },
  { key: 'SHIPPING_MATERIAL', label: 'Shipping Materials', icon: <InventoryOutlinedIcon />, color: '#ff9800' }
];

export default function InventoryDefaultsPanel() {
  const [units, setUnits] = useState({});
  const [locations, setLocations] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [newUnit, setNewUnit] = useState({});
  const [newLoc, setNewLoc] = useState('');
  const [newCol, setNewCol] = useState('');

  // snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState({ open: false, type: '', item: '', key: '' });
  
  const showSnackbar = (msg, severity = 'success') => {
    setSnackbarMsg(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Load settings from DynamoDB
  useEffect(() => {
    async function loadSettings() {
      setLoading(true);
      try {
        const settings = await getSettings();
        setUnits(settings.units || {});
        setLocations(settings.locations || []);
        setCollections(settings.collections || []);
      } catch (error) {
        console.error('Error loading inventory settings:', error);
        showSnackbar('Failed to load settings', 'danger');
      } finally {
        setLoading(false);
      }
    }
    
    loadSettings();
  }, []);

  // Save settings to DynamoDB
  const persist = async (changes) => {
    setSaving(true);
    try {
      const merged = { units, locations, collections, ...changes };
      await setSettings(merged);
      setSaving(false);
    } catch (error) {
      console.error('Error saving inventory settings:', error);
      showSnackbar('Failed to save settings', 'danger');
      setSaving(false);
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  const addUnit = async (key) => {
    if (!newUnit[key]) return;
    
    // Prevent duplicates
    if ((units[key] || []).includes(newUnit[key])) {
      showSnackbar(`Unit "${newUnit[key]}" already exists for ${ITEM_TYPES.find(t => t.key === key).label}`, 'warning');
      return;
    }
    
    const updated = {
      ...units,
      [key]: [...(units[key] || []), newUnit[key]]
    };
    
    setUnits(updated);
    try {
      await persist({ units: updated });
      setNewUnit(u => ({ ...u, [key]: '' }));
      showSnackbar(`Unit "${newUnit[key]}" added`);
    } catch (error) {
      // Error is already handled in persist function
    }
  };
  
  const confirmDeleteUnit = (key, u) => {
    setDeleteModal({
      open: true,
      type: 'unit',
      item: u,
      key: key
    });
  };

  const delUnit = async (key, u) => {
    const updated = {
      ...units,
      [key]: (units[key] || []).filter(x => x !== u)
    };
    setUnits(updated);
    try {
      await persist({ units: updated });
      showSnackbar(`Unit "${u}" removed`);
      setDeleteModal({ open: false, type: '', item: '', key: '' });
    } catch (error) {
      // Error is already handled in persist function
    }
  };

  const addLoc = async () => {
    if (!newLoc) return;
    
    // Prevent duplicates
    if (locations.includes(newLoc)) {
      showSnackbar(`Location "${newLoc}" already exists`, 'warning');
      return;
    }
    
    const updated = [...locations, newLoc];
    setLocations(updated);
    try {
      await persist({ locations: updated });
      setNewLoc('');
      showSnackbar(`Location "${newLoc}" added`);
    } catch (error) {
      // Error is already handled in persist function
    }
  };
  
  const confirmDeleteLocation = (l) => {
    setDeleteModal({
      open: true,
      type: 'location',
      item: l,
      key: ''
    });
  };

  const delLoc = async (l) => {
    const updated = locations.filter(x => x !== l);
    setLocations(updated);
    try {
      await persist({ locations: updated });
      showSnackbar(`Location "${l}" removed`);
      setDeleteModal({ open: false, type: '', item: '', key: '' });
    } catch (error) {
      // Error is already handled in persist function
    }
  };

  const addCol = async () => {
    if (!newCol) return;
    
    // Prevent duplicates
    if (collections.includes(newCol)) {
      showSnackbar(`Collection "${newCol}" already exists`, 'warning');
      return;
    }
    
    const updated = [...collections, newCol];
    setCollections(updated);
    try {
      await persist({ collections: updated });
      setNewCol('');
      showSnackbar(`Collection "${newCol}" added`);
    } catch (error) {
      // Error is already handled in persist function
    }
  };
  
  const confirmDeleteCollection = (c) => {
    setDeleteModal({
      open: true,
      type: 'collection',
      item: c,
      key: ''
    });
  };
  
  const delCol = async (c) => {
    const updated = collections.filter(x => x !== c);
    setCollections(updated);
    try {
      await persist({ collections: updated });
      showSnackbar(`Collection "${c}" removed`);
      setDeleteModal({ open: false, type: '', item: '', key: '' });
    } catch (error) {
      // Error is already handled in persist function
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size="lg" sx={{ color: '#4caf50' }} />
        <Typography level="h4" sx={{ ml: 2, color: 'text.secondary' }}>
          Loading inventory settings...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography level="h3" sx={{ color: 'white', mb: 1 }}>
          Unit Definitions
        </Typography>
        <Typography level="body-md" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
          About These Settings
        </Typography>
      </Box>

      {/* Unit Definitions Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 2, 
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            color: 'white'
          }}
        >
          <CategoryIcon sx={{ mr: 1, color: '#4caf50' }} />
          Unit Definitions
        </Typography>
        
        <Grid container spacing={3}>
          {ITEM_TYPES.map(t => (
            <Grid xs={12} md={4} key={t.key}>
              <Card 
                variant="soft"
                sx={{ 
                  bgcolor: `rgba(${parseInt(t.color.slice(1, 3), 16)}, ${parseInt(t.color.slice(3, 5), 16)}, ${parseInt(t.color.slice(5, 7), 16)}, 0.1)`,
                  border: `1px solid rgba(${parseInt(t.color.slice(1, 3), 16)}, ${parseInt(t.color.slice(3, 5), 16)}, ${parseInt(t.color.slice(5, 7), 16)}, 0.3)`,
                  borderRadius: 'lg',
                  overflow: 'visible',
                  height: '100%'
                }}
              >
                <Box sx={{ 
                  p: 2,
                  bgcolor: `rgba(${parseInt(t.color.slice(1, 3), 16)}, ${parseInt(t.color.slice(3, 5), 16)}, ${parseInt(t.color.slice(5, 7), 16)}, 0.15)`,
                  borderBottom: `1px solid rgba(${parseInt(t.color.slice(1, 3), 16)}, ${parseInt(t.color.slice(3, 5), 16)}, ${parseInt(t.color.slice(5, 7), 16)}, 0.2)`,
                  borderRadius: 'lg lg 0 0',
                  textAlign: 'center'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ mr: 1, color: t.color }}>{t.icon}</Box>
                    <Typography level="title-md" sx={{ fontWeight: 'bold', color: t.color }}>
                      {t.label}
                    </Typography>
                  </Box>
                </Box>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1, 
                    mb: 2, 
                    minHeight: 100
                  }}>
                    {(units[t.key] || []).length > 0 ? (
                      (units[t.key] || []).map(u => (
                        <Chip
                          key={u}
                          variant="soft"
                          endDecorator={
                            <IconButton 
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmDeleteUnit(t.key, u);
                              }}
                              sx={{ 
                                color: 'rgba(255, 255, 255, 0.7)',
                                minWidth: 24,
                                minHeight: 24,
                                p: 0.5,
                                borderRadius: 'sm',
                                '&:hover': { 
                                  color: '#f44336',
                                  bgcolor: 'rgba(244, 67, 54, 0.1)',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          }
                          sx={{ 
                            bgcolor: `rgba(${parseInt(t.color.slice(1, 3), 16)}, ${parseInt(t.color.slice(3, 5), 16)}, ${parseInt(t.color.slice(5, 7), 16)}, 0.15)`,
                            color: 'white',
                            '&:hover': { bgcolor: `rgba(${parseInt(t.color.slice(1, 3), 16)}, ${parseInt(t.color.slice(3, 5), 16)}, ${parseInt(t.color.slice(5, 7), 16)}, 0.2)` }
                          }}
                        >
                          {u}
                        </Chip>
                      ))
                    ) : (
                      <Typography level="body-sm" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No custom units defined
                      </Typography>
                    )}
                  </Box>
                  
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Input
                      size="sm"
                      placeholder="New Unit"
                      value={newUnit[t.key] || ''}
                      onChange={e => setNewUnit(u => ({ ...u, [t.key]: e.target.value }))}
                      onKeyPress={e => handleKeyPress(e, () => addUnit(t.key))}
                      startDecorator={<SquareFootIcon sx={{ color: `rgba(${parseInt(t.color.slice(1, 3), 16)}, ${parseInt(t.color.slice(3, 5), 16)}, ${parseInt(t.color.slice(5, 7), 16)}, 0.7)` }} />}
                      sx={{ 
                        flexGrow: 1,
                        borderRadius: 'md',
                        '& .MuiInput-root': {
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&.Mui-focused': {
                            borderColor: t.color,
                          },
                        }
                      }}
                    />
                    <Button
                      variant="solid"
                      size="sm"
                      onClick={() => addUnit(t.key)}
                      disabled={!newUnit[t.key] || saving}
                      sx={{ 
                        minWidth: 40,
                        bgcolor: t.color,
                        borderRadius: 'md',
                        '&:hover': {
                          bgcolor: `rgba(${parseInt(t.color.slice(1, 3), 16)}, ${parseInt(t.color.slice(3, 5), 16)}, ${parseInt(t.color.slice(5, 7), 16)}, 0.8)`
                        }
                      }}
                    >
                      {saving ? <CircularProgress size="sm" /> : <AddIcon />}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      
      {/* Location Presets */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 2, 
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            textAlign: 'center'
          }}
        >
          <LocationOnIcon sx={{ mr: 1, color: '#2196f3' }} />
          Location Presets
        </Typography>
        
        <Card 
          variant="soft"
          sx={{ 
            bgcolor: 'rgba(33, 150, 243, 0.1)',
            border: '1px solid rgba(33, 150, 243, 0.3)',
            borderRadius: 'lg',
            overflow: 'visible',
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              mb: 2, 
              minHeight: 80
            }}>
              {locations.length > 0 ? (
                locations.map(l => (
                  <Chip
                    key={l}
                    variant="soft"
                    endDecorator={
                                          <IconButton 
                      size="sm" 
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDeleteLocation(l);
                      }}
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        minWidth: 24,
                        minHeight: 24,
                        p: 0.5,
                        borderRadius: 'sm',
                        '&:hover': { 
                          color: '#f44336',
                          bgcolor: 'rgba(244, 67, 54, 0.1)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    }
                    sx={{ 
                      bgcolor: 'rgba(33, 150, 243, 0.15)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.2)' }
                    }}
                  >
                    {l}
                  </Chip>
                ))
              ) : (
                <Typography level="body-sm" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No location presets defined
                </Typography>
              )}
            </Box>
            
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Input
                placeholder="New Location"
                value={newLoc}
                onChange={e => setNewLoc(e.target.value)}
                onKeyPress={e => handleKeyPress(e, addLoc)}
                startDecorator={<LocationOnIcon sx={{ color: 'rgba(33, 150, 243, 0.7)' }} />}
                sx={{ 
                  flexGrow: 1,
                  borderRadius: 'md',
                  '& .MuiInput-root': {
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused': {
                      borderColor: '#2196f3',
                    },
                  }
                }}
              />
              <Button
                variant="solid"
                onClick={addLoc}
                disabled={!newLoc || saving}
                sx={{ 
                  px: 3,
                  py: 1.5,
                  borderRadius: 'md',
                  bgcolor: '#2196f3',
                  '&:hover': {
                    bgcolor: 'rgba(33, 150, 243, 0.8)'
                  }
                }}
              >
                {saving ? (
                  <CircularProgress size="sm" sx={{ color: 'white' }} />
                ) : (
                  <>
                    <AddIcon sx={{ mr: 0.5 }} />
                    Add
                  </>
                )}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
      
      {/* Collections Management */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 2, 
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            textAlign: 'center'
          }}
        >
          <ClassIcon sx={{ mr: 1, color: '#9c27b0' }} />
          Collections Management
        </Typography>
        
        <Card 
          variant="soft"
          sx={{ 
            bgcolor: 'rgba(156, 39, 176, 0.1)',
            border: '1px solid rgba(156, 39, 176, 0.3)',
            borderRadius: 'lg',
            overflow: 'visible',
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              mb: 2, 
              minHeight: 80
            }}>
              {collections.length > 0 ? (
                collections.map(c => (
                  <Chip
                    key={c}
                    variant="soft"
                    endDecorator={
                      <IconButton 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDeleteCollection(c);
                        }}
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.7)',
                          minWidth: 24,
                          minHeight: 24,
                          p: 0.5,
                          borderRadius: 'sm',
                          '&:hover': { 
                            color: '#f44336',
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    }
                    sx={{ 
                      bgcolor: 'rgba(156, 39, 176, 0.15)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.2)' }
                    }}
                  >
                    {c}
                  </Chip>
                ))
              ) : (
                <Typography level="body-sm" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No collections defined
                </Typography>
              )}
            </Box>
            
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Input
                placeholder="New Collection"
                value={newCol}
                onChange={e => setNewCol(e.target.value)}
                onKeyPress={e => handleKeyPress(e, addCol)}
                startDecorator={<LocalOfferIcon sx={{ color: 'rgba(156, 39, 176, 0.7)' }} />}
                sx={{ 
                  flexGrow: 1,
                  borderRadius: 'md',
                  '& .MuiInput-root': {
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused': {
                      borderColor: '#9c27b0',
                    },
                  }
                }}
              />
              <Button
                variant="solid"
                onClick={addCol}
                disabled={!newCol || saving}
                sx={{ 
                  px: 3,
                  py: 1.5,
                  borderRadius: 'md',
                  bgcolor: '#9c27b0',
                  '&:hover': {
                    bgcolor: 'rgba(156, 39, 176, 0.8)'
                  }
                }}
              >
                {saving ? (
                  <CircularProgress size="sm" sx={{ color: 'white' }} />
                ) : (
                  <>
                    <AddIcon sx={{ mr: 0.5 }} />
                    Add
                  </>
                )}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Help Info Box */}
      <Sheet 
        variant="soft"
        sx={{
          p: 2,
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 'lg',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography level="title-sm" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'bold', mb: 1 }}>
          About These Settings
        </Typography>
        <Typography level="body-sm" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          These settings will be used throughout the app to provide options when adding or editing inventory items. Units and locations can be selected when creating items, and collections help organize your seed catalog.
        </Typography>
      </Sheet>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, type: '', item: '', key: '' })}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <ModalDialog
          variant="soft"
          sx={{
            maxWidth: 400,
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <ModalClose />
          
          <Typography level="h4" sx={{ mb: 2, color: 'white' }}>
            Confirm Deletion
          </Typography>
          
          <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
            Are you sure you want to delete the {deleteModal.type} "{deleteModal.item}"? 
            This action cannot be undone.
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button 
              variant="soft"
              color="neutral"
              onClick={() => setDeleteModal({ open: false, type: '', item: '', key: '' })}
            >
              Cancel
            </Button>
            <Button 
              variant="solid"
              color="danger"
              onClick={() => {
                if (deleteModal.type === 'unit') {
                  delUnit(deleteModal.key, deleteModal.item);
                } else if (deleteModal.type === 'location') {
                  delLoc(deleteModal.item);
                } else if (deleteModal.type === 'collection') {
                  delCol(deleteModal.item);
                }
              }}
            >
              Delete
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        color={snackbarSeverity}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          color={snackbarSeverity}
          variant="solid"
          sx={{ width: '100%' }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
